import * as RadixPopover from '@radix-ui/react-popover';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

type PopoverProps = {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
};

function Popover({ children, ...rootProps }: PopoverProps) {
  return <RadixPopover.Root {...rootProps}>{children}</RadixPopover.Root>;
}

type TriggerProps = ComponentPropsWithoutRef<typeof RadixPopover.Trigger>;
function Trigger(props: TriggerProps) {
  return <RadixPopover.Trigger asChild {...props} />;
}

type ContentProps = Omit<
  ComponentPropsWithoutRef<typeof RadixPopover.Content>,
  'side' | 'align' | 'sideOffset' | 'collisionPadding'
> & {
  side?: Side;
  align?: Align;
  sideOffset?: number;
  collisionPadding?: number;
};

function Content({
  className,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  collisionPadding = 8,
  children,
  ...rest
}: ContentProps) {
  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={`z-popover rounded-md bg-popover p-3 text-sm text-popover-foreground shadow-popover focus:outline-none data-[state=open]:animate-[popover-fade-in_180ms_ease-out] ${className ?? ''}`}
        {...rest}
      >
        {children}
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
}

const Close = RadixPopover.Close;
const Anchor = RadixPopover.Anchor;

Popover.Trigger = Trigger;
Popover.Content = Content;
Popover.Close = Close;
Popover.Anchor = Anchor;

export { Popover };
